import { useQuery } from '@tanstack/react-query'
import { Button, Card, Image, Tooltip } from 'antd'
import { AnimatePresence, motion } from 'framer-motion'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FaBatteryEmpty, FaChevronLeft, FaEye, FaHeart, FaMousePointer, FaVideo } from 'react-icons/fa'
import { FaInfoCircle } from 'react-icons/fa'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { GetPostById } from '../../api/posts'
import LoadingSpinner from '../../components/LoadingSpinner'
import useContestantScore from '../ContestantList/hooks/useContestantScore'
import VideoModal from './VideoModal'

export default function ContestantDetails() {
    const { id } = useParams()
    const { t } = useTranslation('ContestantDetails')
    const navigate = useNavigate()
    const [isVideoModalOpen, setIsVideoModalOpen] = useState(false)
    const { getScoreById } = useContestantScore()

    const { data, isPending, isError, refetch } = useQuery({
        queryKey: ['singleContestant', id],
        queryFn: () => GetPostById(id ?? '')
    })

    if (isPending) {
        return (
            <div className="flex h-full w-full flex-col items-center justify-center">
                <LoadingSpinner isLoading={isPending} refresh={refetch} className="spinner-1" />
                <h5 className="mt-8 text-2xl font-bold">Loading Data</h5>
            </div>
        )
    }

    if (isError) {
        return (
            <div className="flex w-full flex-col items-center gap-3 text-center">
                <h2 className="text-xl font-bold">Error loading data</h2>
                <Button type="primary" onClick={() => refetch()}>
                    Try again
                </Button>
            </div>
        )
    }

    if (!data) {
        return (
            <div className="flex w-full flex-col items-center gap-3 text-center">
                <FaBatteryEmpty size={100} />
                <h2 className="text-xl font-bold">{t('This contestant does not exist.')}</h2>
                <Button type="default" onClick={() => navigate('/contestants')}>
                    {t('Return to list')}
                </Button>
            </div>
        )
    }

    return (
        <>
            <AnimatePresence mode="wait">
                <motion.div
                    className="flex w-full flex-col gap-6 lg:flex-row"
                    initial={{ opacity: 0, y: 50 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 50 }}
                    transition={{ duration: 0.3 }}
                >
                    <div className="w-full lg:w-7/12">
                        <Link
                            to={{
                                pathname: '/contestants'
                            }}
                            className="mb-5 inline-flex items-center gap-1"
                        >
                            <FaChevronLeft /> {t('Go back')}
                        </Link>
                        <div className=":w-[500px] aspect-square w-full">
                            <Image
                                src={data.image_url}
                                alt="Contestant"
                                preview={{
                                    mask: (
                                        <div className="flex items-center gap-3">
                                            <FaEye size={16} /> {t('Preview')}
                                        </div>
                                    )
                                }}
                                width="100%"
                                height="100%"
                                className="rounded-lg object-cover shadow-xl"
                            />
                        </div>
                    </div>
                    <div className="mt-6">
                        <p id="copy-id" className="inline cursor-pointer select-none text-xs transition-all hover:tracking-[3px]">
                            {t('Contestant')} #{data.yearofbirth}
                        </p>
                        <h2 className="mb-6 text-4xl">{data.name}</h2>
                        <Card
                            className="bg-transparent text-white"
                            actions={[
                                <Tooltip
                                    title={
                                        <div className="text-center">
                                            <h5 className="text-lg font-bold">{t('Reach')}</h5>
                                            <div className="mt-1 w-32 text-xs font-light text-neutral-600">
                                                {t('Please refresh the page to get new data.')}
                                            </div>
                                        </div>
                                    }
                                    placement="bottom"
                                >
                                    <div className="flex items-center justify-center gap-3">
                                        <FaMousePointer /> {getScoreById(data.yearofbirth.toString())?.reach ?? '0'}
                                    </div>
                                </Tooltip>,
                                <Tooltip
                                    title={
                                        <div className="text-center">
                                            <h5 className="text-lg font-bold">{t('Engagement')}</h5>
                                            <div className="mt-1 w-32 text-xs font-light text-neutral-600">
                                                {t('Please refresh the page to get new data.')}
                                            </div>
                                        </div>
                                    }
                                    placement="bottom"
                                >
                                    <div className="flex items-center justify-center gap-3">
                                        <FaHeart /> {getScoreById(data.yearofbirth.toString())?.engagement ?? '0'}
                                    </div>
                                </Tooltip>
                            ]}
                        >
                            <div className="">
                                <div className="group mb-3">
                                    {t("Hi, I'm")} <span className="font-bold">{data.name}</span> {t('and my ID is')} {data.yearofbirth}
                                </div>
                                {/^(https?:\/\/)?([\w-]+(\.[\w-]+)+\/?)([\w-./?%&=]*)?$/.test(data.hometown) && (
                                    <Button
                                        type="primary"
                                        className="flex w-max items-center gap-3"
                                        onClick={() => window.open(data.hometown, '_blank', 'noopener noreferrer')}
                                    >
                                        <FaVideo /> {t('Link to Post')}
                                    </Button>
                                )}
                            </div>
                        </Card>
                    </div>
                </motion.div>
            </AnimatePresence>
            <VideoModal isOpen={isVideoModalOpen} handleClose={() => setIsVideoModalOpen(false)} videoSrc={data.video_url} />
        </>
    )
}
